import { Component } from 'react'
// @ts-expect-error untyped library
import { Helmet } from 'react-helmet'
import { compose } from 'redux'
import { appConnect } from '~/store/hooks'
import * as filestack from 'filestack-js'
import { graphql } from '@apollo/client/react/hoc'
import get from 'lodash-es/get'

import { GET_CLOUDINARY_SIGNATURE_QUERY } from 'client/queries'

import FILESTACK_API_KEY from 'config/filestack'
import routes from 'config/routes'
import { getTitle } from 'config/titles'
import cloudinary from 'config/cloudinary'
import sendToSentry from 'lib/sendToSentry'
import LAYER_KEYS from 'config/layerKeys'
import { convert } from '~/lib/conversion/pdf'

import { saveBackgroundImageFile } from 'store/backgroundImageForm'
import { mostRecentSelectedObject } from 'store/selectedObjects/selectors'
import { addBackgroundImage, addMetadataImage } from 'store/objects'

import { Modal, MODAL_ANIMATION_VARIANT } from '~/ui/Modal'
import { Dialog } from '~/ui/Dialog'
import { Heading } from 'react-aria-components'
import { withNavigate, withParams } from '~/utils/reactRouterHOC'
import { AppDispatch } from '~/store'
import { NavigateFunction } from 'react-router-dom'

class UploadImageModal extends Component<{
  cloudinaryAuth: {
    CloudinarySignature: any
  }
  navigate: NavigateFunction
  params: {
    type: string
  }
  onAddBackgroundImage(attrs: any): void
  onSaveBackgroundImageFile(attrs: any): void
  onUpdateObjectMetadata(attrs: any): void
  props: object
}> {
  state = {
    isOpen: true,
  }
  client!: filestack.Client
  picker!: filestack.PickerInstance

  componentDidMount() {
    this.client = filestack.init(FILESTACK_API_KEY)
    this.createPicker()
    this.openPicker()
  }

  componentWillUnmount() {
    this.closePicker()
  }

  createPicker = () => {
    this.picker = this.client.picker({
      accept: ['image/*', '.pdf', '.dwg'],
      uploadInBackground: false,
      disableTransformer: this.props.params.type === 'background',
      maxFiles: this.props.params.type === 'metadata' ? 10 : 1,
      onUploadDone: this.handleFileUploadFinished,
      fromSources: ['local_file_system', 'googledrive', 'dropbox', 'clouddrive', 'box', 'onedrive', 'picasa'],
      customText: {
        'or Drag and Drop, Copy and Paste Files': 'You can upload .DWG, .PDF or image files.',
      },
      displayMode: filestack.PickerDisplayMode.inline,
      container: '#filestack-ref',
      maxSize: 20 * 1024 * 1024,
      imageMax: [2160, 2160],
    })
  }

  openPicker = () => {
    this.picker.open()
  }

  closePicker = () => {
    this.picker.close()
  }

  handleFileUploadFinished = async (files: filestack.PickerResponse) => {
    await Promise.all(
      files.filesUploaded.map(file =>
        this.client.metadata(file.handle, { width: true, height: true }).then(res =>
          this.handleSave({
            file,
            res,
          })
        )
      )
    )

    this.handleNavigateOnComplete(files)
  }

  handleCloudinaryUpload = async (data: string) => {
    try {
      // Upload image to cloudinary and store the results.
      const { cloudinaryAuth } = this.props
      const { CloudinarySignature = {} } = cloudinaryAuth
      const response = await cloudinary.upload(data, CloudinarySignature)
      const { public_id: cloudinaryId } = await response.json()
      return cloudinaryId
    } catch (error) {
      sendToSentry({
        prefix: 'Cloudinary upload failure',
        error: error as any,
        action: 'UploadImageModal.handleCloudinaryUpload',
      })
      throw error
    }
  }

  handleSave = async ({ file, res }: { file: filestack.PickerFileMetadata; res: any }) => {
    const { url, mimetype, filename } = file
    const { width, height } = res
    const { params, onSaveBackgroundImageFile, onAddBackgroundImage } = this.props

    let convertedUrl = url

    if (mimetype.match('heic')) {
      const splitUrl = convertedUrl.split('/')
      splitUrl.splice(splitUrl.length - 1, 0, 'output=format:jpg')
      convertedUrl = splitUrl.join('/')
    }

    switch (params.type) {
      case 'background':
        if (mimetype.match('pdf')) {
          const buffer = await fetch(convertedUrl).then(it => it.arrayBuffer())
          const image = await convert(buffer)
          const blob = new Blob([image])
          const uploaded = await this.client.upload(blob, {}, { filename: filename + '.png' })
          const metadata = await this.client.metadata(uploaded.handle, { width: true, height: true })
          await this.handleSave({ file: uploaded, res: metadata })
        } else if (filename.match('dwg') || mimetype.match('application')) {
          // Temporarily save the returned data to be used in subsequent modals
          onSaveBackgroundImageFile({ src: convertedUrl, mimetype, filename })
        } else {
          onAddBackgroundImage({
            backgroundImage: {
              BACKGROUND_IMAGE: {
                src: convertedUrl,
                originalWidth: width,
                originalHeight: height,
                width,
                height,
                opacity: 1,
                position: {
                  x: 0,
                  y: 0,
                  z: 0,
                },
                rotation: {
                  x: 0,
                  y: 0,
                  z: 0,
                },
                layerKey: LAYER_KEYS.BACKGROUND_IMAGE,
                id: LAYER_KEYS.BACKGROUND_IMAGE,
              },
            },
          })
        }
        break
      case 'metadata': {
        const selectedObject = mostRecentSelectedObject()
        if (selectedObject) {
          const cloudinaryId = await this.handleCloudinaryUpload(convertedUrl)
          this.props.onUpdateObjectMetadata({
            cloudinaryId,
            objectId: selectedObject.id,
            width,
            height,
          })
        }
        break
      }
      default:
        break
    }
  }

  handleNavigateOnComplete = (files: filestack.PickerResponse) => {
    const { params, navigate } = this.props
    switch (params.type) {
      case 'background': {
        const mimetype = get(files, 'filesUploaded[0].mimetype', '')
        const filename = get(files, 'filesUploaded[0].filename', '')
        if (filename.match('dwg') || (mimetype.match('application') && !mimetype.match('pdf'))) {
          navigate(`../..${routes.modals.convertImage}`, { relative: 'path' })
        } else {
          navigate(`../..${routes.modals.editImage}`, { relative: 'path' })
        }
        break
      }
      case 'metadata':
        this.handleClose()
        break
      default:
        this.handleClose()
        break
    }
  }

  handleClose = () => {
    this.setState({ isOpen: false })
  }

  render() {
    return (
      <div>
        <Helmet>
          <title>{getTitle('uploadImage')}</title>
        </Helmet>
        <Modal
          isDismissable
          isOpen={this.state.isOpen}
          onOpenChange={open => this.setState({ isOpen: open })}
          onAnimationComplete={animation => {
            if (animation === MODAL_ANIMATION_VARIANT.hidden) this.props.navigate('../..', { relative: 'path' })
          }}
          className="max-w-3xl"
        >
          <Dialog>
            <Heading slot="title" className="text-lg pb-3 mb-3 border-b">
              Upload Image
            </Heading>
            <div id="filestack-ref" />
          </Dialog>
        </Modal>
      </div>
    )
  }
}

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onSaveBackgroundImageFile(attrs: any) {
    dispatch(saveBackgroundImageFile(attrs))
  },
  onAddBackgroundImage(attrs: any) {
    dispatch(addBackgroundImage(attrs))
  },
  onUpdateObjectMetadata(attrs: any) {
    dispatch(addMetadataImage({ ...attrs }))
  },
})

export default compose(
  appConnect(null, mapDispatchToProps),
  graphql(GET_CLOUDINARY_SIGNATURE_QUERY, {
    options: {
      fetchPolicy: 'network-only',
    },
    name: 'cloudinaryAuth',
  }),
  withNavigate,
  withParams
)(UploadImageModal) as typeof Component
